/**
 * Sidebar — main navigation panel content.
 *
 * Shared between desktop `ResizablePanel` and mobile `MobileDrawer`.
 * Consumes `useStaticDataImmediate()` for live badge counters, progress data,
 * member avatars, and Areas/Projects/Lists.
 *
 * ## Architecture
 *
 * ONE unified `SidebarItem` component handles all sidebar rows:
 * - Static nav buttons (Inbox, Today, Upcoming, etc.)
 * - Dynamic items (Areas, Projects, Lists)
 *
 * Layout per row:
 * `[Icon 24px] [Label (flex-1, truncate)] [Flag+Date?] [Avatars?] [Badge?]`
 *
 * Areas get a larger top margin to create visual group separation,
 * except when an area is the first item in the dynamic list.
 *
 * ## Mono vs Color
 *
 * Default is "monochrome" — matching v1's shipped behavior where
 * icons are gray (inactive) or primary-colored (active).
 */

import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useObservable } from "dexie-react-hooks"
import { useTranslation } from "react-i18next"
import ChevronBigRight from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-big-right.svg?react"
import ChevronBigDown from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-big-down.svg?react"

import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { ROUTES } from "@/config/routes"
import {
  preloadInbox,
  preloadUpcoming,
  preloadSomeday,
  preloadAnyday,
  preloadLogbook,
  preloadTrash,
} from "@/app/routes"
import { cn } from "@/shared/utils/general/cn"
import { useScrollbarAutoHide } from "@/shared/hooks/useScrollbarAutoHide"
import { StaticIcon, type IconMode } from "@/shared/icons/StaticIcon"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import { db } from "@/db/schema"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { SidebarItemContent } from "@/features/navigation/SidebarItemContent"

import { AddMenu } from "@/features/navigation/AddMenu"
import { useNewTaskDots } from "@/features/views/hooks/useNewTaskDots"
import { viewSeenKey } from "@/features/views/stores/viewSeenStore"
import {
  addTask,
  addProject,
  addList,
  addArea,
  scheduleTask,
} from "@/db/operations"
import { getTaskDefaults } from "@/shared/utils/task/getTaskDefaults"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useViewContext } from "@/app/stores/viewContext"
import {
  useStaticDataImmediate,
  type Counters,
  type MenuItem,
} from "@/features/static-data/useStaticData"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { useAncestorTagStore } from "@/features/tags/ancestorTagStore"
import { SidebarLicense } from "@/features/navigation/SidebarLicense"
import { GlobalTimerBar } from "@/features/time-tracking/GlobalTimerBar"
import { useActiveTimers } from "@/features/time-tracking/useActiveTimer"
import { SidebarDndContext } from "@/features/navigation/SidebarDndContext"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { SortableSidebarItem } from "@/features/navigation/SortableSidebarItem"
import { SwipeableSidebarItem, type SidebarSwipeAction } from "@/features/navigation/SwipeableSidebarItem"
import { SidebarContextMenu } from "@/features/navigation/SidebarContextMenu"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { MobileTopBar } from "@/shared/components/MobileTopBar"
import { toast } from "sonner"

// ---------------------------------------------------------------------------
// Static navigation items — match v1 sidebar groups:
//   [Inbox] ── gap
//   [Today, Upcoming, Anyday, Someday] ── gap
//   [Logbook, Trash]
// ---------------------------------------------------------------------------

interface NavItemDef {
  to: string
  label: string
  icon: RegisteredIconType
  /** Gap after this item (matches v1 visual groups) */
  gap?: boolean
  /** Key into Counters for badge count */
  badge?: keyof Counters
  /** Sidebar drop target identifier for DnD */
  dropTarget?: string
  /** Preload function — called on mouse enter to prefetch the route chunk */
  preload?: () => void
}

const NAV_ITEMS: NavItemDef[] = [
  {
    to: ROUTES.INBOX,
    label: "Inbox",
    icon: "inbox",
    gap: true,
    badge: "inbox",
    dropTarget: "inbox",
    preload: preloadInbox,
  },
  {
    to: ROUTES.TODAY,
    label: "Today",
    icon: "today",
    badge: "today",
    dropTarget: "today",
  },
  {
    to: ROUTES.UPCOMING,
    label: "Upcoming",
    icon: "upcoming",
    dropTarget: "upcoming",
    preload: preloadUpcoming,
  },
  { to: ROUTES.ANYDAY, label: "Anyday", icon: "anyday", dropTarget: "anyday", preload: preloadAnyday },
  {
    to: ROUTES.SOMEDAY,
    label: "Someday",
    icon: "someday",
    dropTarget: "someday",
    preload: preloadSomeday,
    gap: true,
  },
]

/** Logbook — always visible below the main nav items */
const LOGBOOK_ITEM: NavItemDef = {
  to: ROUTES.LOGBOOK,
  label: "Logbook",
  icon: "logbook",
  dropTarget: "logbook",
  preload: preloadLogbook,
}

/** More nav items — hidden behind "···" toggle */
const MORE_NAV_ITEMS: NavItemDef[] = [
  { to: ROUTES.RECURRING, label: "Recurring", icon: "recurring" },
  { to: ROUTES.ROUTINES, label: "Routines", icon: "routines" },
  { to: ROUTES.TRASH, label: "Trash", icon: "trash", dropTarget: "trash", preload: preloadTrash },
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SidebarProps {
  /** Called after a nav item is clicked (e.g. close mobile drawer) */
  onNavigate?: () => void
  /** Icon display mode — "monochrome" (default) or "color" */
  iconMode?: IconMode
}

// ---------------------------------------------------------------------------
// Sidebar root
// ---------------------------------------------------------------------------

export function Sidebar({ onNavigate, iconMode = "monochrome" }: SidebarProps) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const location = useLocation()
  const [staticData, setOptimistic, isLoading] = useStaticDataImmediate()
  const { menuItems, counters, memberMap } = staticData
  const newTaskDots = useNewTaskDots(menuItems)
  const { ref: sidebarScrollRef, onScroll } =
    useScrollbarAutoHide<HTMLDivElement>()

  // Dismiss "Preparing your views..." toast when sidebar data is ready
  const sidebarReadyRef = useRef(false)
  useEffect(() => {
    if (!isLoading && !sidebarReadyRef.current) {
      sidebarReadyRef.current = true
      toast.dismiss("preparing-views")
    }
  }, [isLoading])

  // ── Pending invites (v1 pattern: useObservable + filter) ──────────────
  const allInvites = useObservable(db.cloud.invites)
  const pendingInvites = useMemo(
    () => allInvites?.filter((i) => !i.accepted && !i.rejected) ?? [],
    [allInvites],
  )

  // Feed ancestor tag store whenever menu items change
  useEffect(() => {
    useAncestorTagStore.getState().rebuildFromMenu(menuItems)
  }, [menuItems])

  // ── Collapsed area state (persisted in localStorage) ──────────────────
  const [collapsedAreas, setCollapsedAreas] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("totodo:collapsed-areas")
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set()
    } catch {
      return new Set()
    }
  })

  const toggleAreaCollapse = useCallback((areaId: string) => {
    setCollapsedAreas((prev) => {
      const next = new Set(prev)
      if (next.has(areaId)) {
        next.delete(areaId)
      } else {
        next.add(areaId)
      }
      localStorage.setItem("totodo:collapsed-areas", JSON.stringify([...next]))
      return next
    })
  }, [])

  // ── Secondary nav collapsed state (persisted) ─────────────────────────
  const [moreCollapsed, setMoreCollapsed] = useState(() => {
    try {
      return localStorage.getItem("totodo:sidebar-more") !== "0"
    } catch {
      return true
    }
  })

  const toggleMore = useCallback(() => {
    setMoreCollapsed((prev) => {
      localStorage.setItem("totodo:sidebar-more", prev ? "0" : "1")
      return !prev
    })
  }, [])

  // ── Sidebar selection state (for floating Done bar) ───────────────────
  const selectedSidebarIds = useViewContext((s) => s.selectedSidebarIds)
  const clearSidebarSelection = useViewContext((s) => s.clearSidebarSelection)
  const sidebarSelectionMode = selectedSidebarIds.length > 0

  // Sidebar background per icon mode
  // Color: neutral gray (light = --muted, dark = foreground at 3% opacity)
  // Mono:  primary-tinted --sidebar
  // ~3 % matches Linear / VS Code: subtle zone without color tint.
  const sidebarBg =
    iconMode === "color" ? "bg-[var(--sidebar-color)]" : "bg-sidebar"

  // Expose the actual sidebar background as a CSS variable so child
  // components (avatars, badges) can match their border color to it.
  const sidebarSurfaceStyle =
    iconMode === "color"
      ? ({
          "--sidebar-surface": "var(--sidebar-color)",
        } as React.CSSProperties)
      : ({ "--sidebar-surface": "var(--sidebar)" } as React.CSSProperties)

  return (
    <nav
      data-sidebar-panel
      className={cn(
        "flex h-full min-h-0 flex-col",
        sidebarBg,
        isMobile && "safe-area-top",
      )}
      style={sidebarSurfaceStyle}
    >
      {/* Selection mode bar — mirrors TopNavBar task selection (iOS standard).
          The parent <nav> already has safe-area-top padding so we override
          MobileTopBar's internal status-bar inset to avoid doubling. */}
      {sidebarSelectionMode && (
        <MobileTopBar
          bordered
          style={{ paddingTop: 0, height: "50px" }}
          center={
            <span className="text-[17px] font-semibold leading-tight">
              {t("{{count}} Selected", { count: selectedSidebarIds.length })}
            </span>
          }
          right={
            <button
              type="button"
              onClick={() => clearSidebarSelection()}
              className="shrink-0 text-[15px] font-semibold text-primary"
              aria-label={t("Done")}
            >
              {t("Done")}
            </button>
          }
        />
      )}

      {/* Scrollable content area */}
      <div
        ref={sidebarScrollRef}
        onScroll={onScroll}
        className="no-scrollbar min-h-0 flex-1 overflow-y-auto pt-10"
      >
        {/* Static nav buttons */}
        <ul className="mt-[30px] px-5">
          {NAV_ITEMS.map(({ to, label, icon, gap, badge, dropTarget, preload }) => {
            const isActive = isRouteActive(location.pathname, to)
            const badgeCount = badge ? counters[badge] : 0

            return (
              <li key={to} className={gap ? "mb-2.5" : undefined}>
                <SidebarItem
                  to={to}
                  isActive={isActive}
                  iconMode={iconMode}
                  onNavigate={onNavigate}
                  onMouseEnter={preload}
                  icon={
                    <StaticIcon type={icon} active={isActive} mode={iconMode} />
                  }
                  label={t(label)}
                  badge={badgeCount}
                  alert={
                    to === ROUTES.TODAY ? counters.todayDeadline : undefined
                  }
                  dot={dropTarget ? newTaskDots.has(dropTarget) : false}
                  dropTarget={dropTarget}
                />
              </li>
            )
          })}

          {/* Logbook — with chevron to expand/collapse secondary items */}
          <li>
            <SidebarItem
              to={LOGBOOK_ITEM.to}
              isActive={isRouteActive(location.pathname, LOGBOOK_ITEM.to)}
              iconMode={iconMode}
              onNavigate={onNavigate}
              onMouseEnter={LOGBOOK_ITEM.preload}
              icon={
                <StaticIcon
                  type={LOGBOOK_ITEM.icon}
                  active={isRouteActive(location.pathname, LOGBOOK_ITEM.to)}
                  mode={iconMode}
                />
              }
              label={t(LOGBOOK_ITEM.label)}
              dropTarget={LOGBOOK_ITEM.dropTarget}
              trailing={
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleMore()
                  }}
                  className="flex shrink-0 items-center justify-center rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
                  aria-label={moreCollapsed ? t("Show more") : t("Show less")}
                >
                  <ChevronBigDown
                    width={16}
                    height={16}
                    className={cn(
                      "transition-transform duration-200",
                      moreCollapsed ? "rotate-0" : "-rotate-180",
                    )}
                  />
                </button>
              }
            />
          </li>

          {/* Secondary nav — collapsible */}
          {!moreCollapsed &&
            MORE_NAV_ITEMS.map(({ to, label, icon, dropTarget, preload }) => {
              const isActive = isRouteActive(location.pathname, to)
              return (
                <li key={to}>
                  <SidebarItem
                    to={to}
                    isActive={isActive}
                    iconMode={iconMode}
                    onNavigate={onNavigate}
                    onMouseEnter={preload}
                    icon={<StaticIcon type={icon} active={isActive} mode={iconMode} />}
                    label={t(label)}
                    dropTarget={dropTarget}
                  />
                </li>
              )
            })}

          {/* Invites — shown only when there are pending invites (v1 pattern) */}
          {pendingInvites.length > 0 && (
            <li className="mt-2.5">
              <SidebarItem
                to={ROUTES.INVITES}
                isActive={isRouteActive(location.pathname, ROUTES.INVITES)}
                iconMode={iconMode}
                onNavigate={onNavigate}
                icon={
                  <span className="relative">
                    <StaticIcon
                      type="invites"
                      active={isRouteActive(location.pathname, ROUTES.INVITES)}
                      mode={iconMode}
                    />
                    <span
                      className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive"
                      style={{
                        boxShadow:
                          "0 0 0 1.5px var(--sidebar-surface, var(--sidebar))",
                      }}
                    />
                  </span>
                }
                label={t("Invites")}
                badge={pendingInvites.length}
              />
            </li>
          )}
        </ul>

        {/* Dynamic sections: Areas → Projects → Lists (DnD-enabled) */}
        {(menuItems.length > 0 || staticData.freeStandingLaterCount > 0) && (
          <SidebarDndContext
            menuItems={menuItems}
            staticData={staticData}
            setOptimistic={setOptimistic}
            freeStandingLaterCount={staticData.freeStandingLaterCount}
          >
            <SidebarDynamicItems
              menuItems={menuItems}
              collapsedAreas={collapsedAreas}
              toggleAreaCollapse={toggleAreaCollapse}
              iconMode={iconMode}
              onNavigate={onNavigate}
              memberMap={memberMap}
              newTaskDots={newTaskDots}
              freeStandingLaterCount={staticData.freeStandingLaterCount}
            />
          </SidebarDndContext>
        )}

        {/* Mobile: inline settings + add menu at bottom of scrollable list */}
        {isMobile && (
          <SidebarMobileFooter
            iconMode={iconMode}
            onNavigate={onNavigate}
          />
        )}

        {/* Bottom safe area — ensures last item isn't flush with edge */}
        <div className="h-20" aria-hidden />
      </div>

      {/* Upgrade prompt for free/trial-expiring users */}
      <SidebarLicense />

      {/* Global timer — visible in sidebar when a timer is running */}
      <SidebarTimerSlot onNavigate={onNavigate} />

      {/* Desktop: fixed bottom bar with "+ Nytt" + settings gear */}
      {!isMobile && (
        <SidebarFooter iconMode={iconMode} onNavigate={onNavigate} />
      )}
    </nav>
  )
}

// ---------------------------------------------------------------------------
// SidebarTimerSlot — shows GlobalTimerBar when a timer is running
// ---------------------------------------------------------------------------

function SidebarTimerSlot({ onNavigate }: { onNavigate?: () => void }) {
  const timerTasks = useActiveTimers()
  const { isMobile } = useBreakpoint()
  if (timerTasks.length === 0) return null
  return (
    <div
      className={isMobile ? "shrink-0 pl-3 pr-[84px]" : "shrink-0 px-3 pb-1"}
      style={isMobile ? { paddingBottom: "calc(4px + env(safe-area-inset-bottom, 0px))" } : undefined}
    >
      <GlobalTimerBar tasks={timerTasks} onNavigate={onNavigate} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// SidebarDynamicItems — flat DnD-enabled rendering of Areas/Projects/Lists
// ---------------------------------------------------------------------------

interface SidebarDynamicItemsProps {
  menuItems: MenuItem[]
  collapsedAreas: Set<string>
  toggleAreaCollapse: (areaId: string) => void
  iconMode: IconMode
  onNavigate?: () => void
  memberMap: Record<string, Array<{ email: string; pending?: boolean }>>
  /** Set of view keys that have unseen new tasks */
  newTaskDots: Set<string>
  /** Count of free-standing later projects/lists (not under any area) */
  freeStandingLaterCount: number
}

/**
 * Renders all dynamic sidebar items (Areas, Projects, Lists) in a flat
 * list with SortableSidebarItem wrappers for DnD reordering.
 *
 * Key design: items are rendered FLAT (not nested) so @dnd-kit's
 * SortableContext can manage them. Area visual grouping is achieved via
 * CSS margin. Collapse is handled per-child with grid-rows animation.
 * Collapsed children are disabled in useSortable (not draggable/droppable).
 */
const SidebarDynamicItems = memo(function SidebarDynamicItems({
  menuItems,
  collapsedAreas,
  toggleAreaCollapse,
  iconMode,
  onNavigate,
  memberMap,
  newTaskDots,
  freeStandingLaterCount,
}: SidebarDynamicItemsProps) {
  const { t } = useTranslation()
  const location = useLocation()

  // ── Sidebar selection ──
  const selectedSidebarIds = useViewContext((s) => s.selectedSidebarIds)
  const toggleSelectSidebar = useViewContext((s) => s.toggleSelectSidebar)
  const rangeSelectSidebar = useViewContext((s) => s.rangeSelectSidebar)
  const clearSidebarSelection = useViewContext((s) => s.clearSidebarSelection)

  const isSelectionMode = selectedSidebarIds.length > 0

  // ── Context menu state ──
  const [ctxMenu, setCtxMenu] = useState<{
    item: MenuItem
    position: { x: number; y: number }
  } | null>(null)

  const handleContextMenu = useCallback(
    (item: MenuItem) => (e: React.MouseEvent) => {
      e.preventDefault()
      // If selection active and right-clicking unselected item, add it
      const sel = useViewContext.getState().selectedSidebarIds
      if (sel.length > 0 && !sel.includes(item.id)) {
        useViewContext.getState().toggleSelectSidebar(item.id)
      }
      setCtxMenu({ item, position: { x: e.clientX, y: e.clientY } })
    },
    [],
  )

  const closeContextMenu = useCallback(() => setCtxMenu(null), [])

  // Ordered IDs for range-select
  const orderedItemIds = useMemo(() => menuItems.map((m) => m.id), [menuItems])

  // Click handler factory — intercepts Ctrl/Shift for multi-select
  const handleItemClick = useCallback(
    (item: MenuItem) => (e: React.MouseEvent) => {
      // Ctrl/⌘+Click — toggle select, no navigation
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        e.stopPropagation()
        toggleSelectSidebar(item.id)
        return
      }
      // Shift+Click — range select, no navigation
      if (e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()
        rangeSelectSidebar(item.id, orderedItemIds)
        return
      }
      // In selection mode: plain click clears selection and navigates
      if (isSelectionMode) {
        clearSidebarSelection()
      }
      // Let default navigation happen (onNavigate called by NavLink)
      if (onNavigate) onNavigate()
    },
    [toggleSelectSidebar, rangeSelectSidebar, clearSidebarSelection, isSelectionMode, onNavigate, orderedItemIds],
  )

  // Swipe handler factory — for SwipeableSidebarItem on mobile
  const handleSwipeAction = useCallback(
    (item: MenuItem) => (action: SidebarSwipeAction) => {
      if (action === "select") {
        toggleSelectSidebar(item.id)
      } else if (action === "contextMenu") {
        // Open context menu at center of viewport (swipe doesn't have pointer coords)
        setCtxMenu({
          item,
          position: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
        })
      }
    },
    [toggleSelectSidebar],
  )

  // Pre-compute area membership, child counts
  type FlatEntry = { kind: "item"; item: MenuItem; areaId: string | null }

  const { flatItems, areaChildCounts } = useMemo(() => {
    let currentAreaId: string | null = null
    const counts = new Map<string, number>()
    const entries: FlatEntry[] = []

    for (const item of menuItems) {
      if (item.type === ObjectType.Area) {
        currentAreaId = item.id
        counts.set(item.id, 0)
        entries.push({ kind: "item", item, areaId: null })
      } else {
        if (currentAreaId) {
          counts.set(currentAreaId, (counts.get(currentAreaId) ?? 0) + 1)
        }
        entries.push({ kind: "item", item, areaId: currentAreaId })
      }
    }

    return { flatItems: entries, areaChildCounts: counts }
  }, [menuItems])

  // Track whether the first rendered item is an area (for mt-5 logic)
  let isFirstDynamic = true
  let laterRowInserted = false

  // Helper: the "{x} later projects" static row, inserted before first area
  const laterProjectsRow =
    freeStandingLaterCount > 0 ? (
      <li key="__later-projects__" className="mt-2.5">
        <SidebarItem
          to={ROUTES.LATER_PROJECTS}
          isActive={location.pathname === ROUTES.LATER_PROJECTS}
          iconMode={iconMode}
          onNavigate={onNavigate}
          icon={
            <StaticIcon
              type="laterProjects"
              active={location.pathname === ROUTES.LATER_PROJECTS}
              mode={iconMode}
            />
          }
          label={
            freeStandingLaterCount === 1
              ? t("1 later project")
              : t("{{count}} later projects", {
                  count: freeStandingLaterCount,
                })
          }
          dimmed
        />
      </li>
    ) : null

  // ── Compute adjacency for connected selection rounding ────────────
  // Same pattern as TaskItem: adjacent selected items share borders.
  const selectedSet = useMemo(() => new Set(selectedSidebarIds), [selectedSidebarIds])
  const selectionEdges = useMemo(() => {
    const first = new Set<string>()
    const last = new Set<string>()
    for (let i = 0; i < flatItems.length; i++) {
      const id = flatItems[i]!.item.id
      if (!selectedSet.has(id)) continue
      const prevId = i > 0 ? flatItems[i - 1]!.item.id : null
      const nextId = i < flatItems.length - 1 ? flatItems[i + 1]!.item.id : null
      if (!prevId || !selectedSet.has(prevId)) first.add(id)
      if (!nextId || !selectedSet.has(nextId)) last.add(id)
    }
    return { first, last }
  }, [flatItems, selectedSet])

  return (
    <>
      <ul className="mt-5 px-5">
        {flatItems.map((entry) => {
          const { item, areaId } = entry
          const isArea = item.type === ObjectType.Area
          const isCollapsed = areaId != null && collapsedAreas.has(areaId)
          const isActive = isDynamicItemActive(location.pathname, item)
          const members = item.realmId ? memberMap[item.realmId] : undefined

          // Connected selection rounding for swipeable wrapper
          const isSel = selectedSet.has(item.id)
          const isFirst = selectionEdges.first.has(item.id)
          const isLast = selectionEdges.last.has(item.id)
          const borderRadiusClass = isSel
            ? isFirst && isLast
              ? "rounded-lg"
              : isFirst
                ? "rounded-t-lg"
                : isLast
                  ? "rounded-b-lg"
                  : "rounded-none"
            : "rounded-lg"

          // Insert "{x} later projects" row before first area
          const insertLaterRow = isArea && !laterRowInserted && laterProjectsRow
          if (isArea) laterRowInserted = true

          // ── Area item ──────────────────────────────────────────────
          if (isArea) {
            const isFirst = isFirstDynamic
            isFirstDynamic = false
            const childCount = areaChildCounts.get(item.id) ?? 0
            const totalChildCount = childCount
            const areaCollapsed = collapsedAreas.has(item.id)

            return (
              <Fragment key={item.id}>
                {insertLaterRow}
                <SortableSidebarItem
                  id={item.id}
                  className={cn(!isFirst && "mt-5")}
                >
                  <SwipeableSidebarItem onAction={handleSwipeAction(item)} borderRadiusClass={borderRadiusClass}>
                  <SidebarItem
                    to={getItemRoute(item)}
                    isActive={isActive}
                    iconMode={iconMode}
                    onNavigate={onNavigate}
                    onClick={handleItemClick(item)}
                    onContextMenu={handleContextMenu(item)}
                    selected={selectedSidebarIds.includes(item.id)}
                    selectionIndex={selectedSidebarIds.includes(item.id) ? selectedSidebarIds.indexOf(item.id) + 1 : undefined}
                    isSelectionMode={isSelectionMode}
                    isFirstSelected={selectionEdges.first.has(item.id)}
                    isLastSelected={selectionEdges.last.has(item.id)}
                    itemId={item.id}
                    icon={
                      <StaticIcon
                        type="area"
                        active={isActive}
                        mode={iconMode}
                        fillOnActive={false}
                      />
                    }
                    label={item.title || t("New Area")}
                    labelEmpty={!item.title}
                    avatars={members}
                    dropTarget={getItemDropTarget(item)}
                    trailing={
                      totalChildCount > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleAreaCollapse(item.id)
                          }}
                          className="flex shrink-0 items-center justify-center rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
                          aria-label={
                            areaCollapsed ? t("Expand area") : t("Collapse area")
                          }
                        >
                          <ChevronBigRight
                            width={16}
                            height={16}
                            className={cn(
                              "transition-transform duration-200",
                              areaCollapsed ? "rotate-0" : "rotate-90",
                            )}
                          />
                        </button>
                      )
                    }
                  />
                  </SwipeableSidebarItem>
                </SortableSidebarItem>
              </Fragment>
            )
          }

          // ── Project/List item ──────────────────────────────────────
          isFirstDynamic = false

          // Children of an area — collapse animation INSIDE sortable wrapper
          // so @dnd-kit transforms on the <li> aren't clipped by overflow-hidden.
          if (areaId != null) {
            return (
              <SortableSidebarItem
                key={item.id}
                id={item.id}
                disabled={isCollapsed}
              >
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-in-out",
                    isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <SwipeableSidebarItem onAction={handleSwipeAction(item)} borderRadiusClass={borderRadiusClass}>
                    <SidebarItem
                      to={getItemRoute(item)}
                      isActive={isActive}
                      iconMode={iconMode}
                      onNavigate={onNavigate}
                      onClick={handleItemClick(item)}
                      onContextMenu={handleContextMenu(item)}
                      selected={selectedSidebarIds.includes(item.id)}
                      selectionIndex={selectedSidebarIds.includes(item.id) ? selectedSidebarIds.indexOf(item.id) + 1 : undefined}
                      isSelectionMode={isSelectionMode}
                      isFirstSelected={selectionEdges.first.has(item.id)}
                      isLastSelected={selectionEdges.last.has(item.id)}
                      itemId={item.id}
                      dimmed={item.isLater}
                      icon={
                        <DynamicIcon
                          icon={item.icon}
                          type={
                            item.type === ObjectType.List ? "list" : "project"
                          }
                          percentage={
                            item.totalCount > 0
                              ? Math.round(
                                  (item.completedCount / item.totalCount) * 100,
                                )
                              : 0
                          }
                          unfinished={item.unfinishedCount}
                          status={item.status}
                          color={
                            isActive && iconMode !== "color"
                              ? "var(--primary)"
                              : "var(--muted-foreground)"
                          }
                        />
                      }
                      label={
                        item.title ||
                        (item.type === ObjectType.List
                          ? t("New List")
                          : t("New Project"))
                      }
                      labelEmpty={!item.title}
                      dueAt={item.dueAt}
                      avatars={members}
                      dot={newTaskDots.has(
                        viewSeenKey(
                          item.type === ObjectType.List ? "list" : "project",
                          item.id,
                        ),
                      )}
                      dropTarget={getItemDropTarget(item)}
                    />
                    </SwipeableSidebarItem>
                  </div>
                </div>
              </SortableSidebarItem>
            )
          }

          // Orphan item (before any area) — no collapse wrapper needed
          return (
            <SortableSidebarItem key={item.id} id={item.id}>
              <SwipeableSidebarItem onAction={handleSwipeAction(item)} borderRadiusClass={borderRadiusClass}>
              <SidebarItem
                to={getItemRoute(item)}
                isActive={isActive}
                iconMode={iconMode}
                onNavigate={onNavigate}
                onClick={handleItemClick(item)}
                onContextMenu={handleContextMenu(item)}
                selected={selectedSidebarIds.includes(item.id)}
                selectionIndex={selectedSidebarIds.includes(item.id) ? selectedSidebarIds.indexOf(item.id) + 1 : undefined}
                isSelectionMode={isSelectionMode}
                isFirstSelected={selectionEdges.first.has(item.id)}
                isLastSelected={selectionEdges.last.has(item.id)}
                itemId={item.id}
                dimmed={item.isLater}
                icon={
                  <DynamicIcon
                    icon={item.icon}
                    type={item.type === ObjectType.List ? "list" : "project"}
                    percentage={
                      item.totalCount > 0
                        ? Math.round(
                            (item.completedCount / item.totalCount) * 100,
                          )
                        : 0
                    }
                    unfinished={item.unfinishedCount}
                    status={item.status}
                    color={
                      isActive && iconMode !== "color"
                        ? "var(--primary)"
                        : "var(--muted-foreground)"
                    }
                  />
                }
                label={
                  item.title ||
                  (item.type === ObjectType.List
                    ? t("New List")
                    : t("New Project"))
                }
                labelEmpty={!item.title}
                dueAt={item.dueAt}
                avatars={members}
                dot={newTaskDots.has(
                  viewSeenKey(
                    item.type === ObjectType.List ? "list" : "project",
                    item.id,
                  ),
                )}
                dropTarget={getItemDropTarget(item)}
              />
              </SwipeableSidebarItem>
            </SortableSidebarItem>
          )
        })}
        {/* Later-projects row at end if no areas triggered it */}
        {!laterRowInserted && laterProjectsRow}
      </ul>
      {/* Context menu portal */}
      {ctxMenu && (
        <SidebarContextMenu
          item={ctxMenu.item}
          selectedIds={selectedSidebarIds}
          position={ctxMenu.position}
          onClose={closeContextMenu}
        />
      )}
    </>
  )
})

// ---------------------------------------------------------------------------
// SidebarItem — THE unified component for all sidebar rows
// ---------------------------------------------------------------------------

interface SidebarItemProps {
  to: string
  isActive: boolean
  iconMode: IconMode
  onNavigate?: () => void
  onMouseEnter?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
  onClick?: (e: React.MouseEvent) => void
  icon: ReactNode
  label: string
  /** Show label in a dimmed style (for empty/untitled items) */
  labelEmpty?: boolean
  /** Badge count (plain number at end of row) */
  badge?: number
  /** Alert count — red circle badge (e.g. deadline count on Today) */
  alert?: number
  /** Due date timestamp — shows flag icon + relative date */
  dueAt?: string
  /** Member avatars for shared items */
  avatars?: Array<{ email: string; pending?: boolean }>
  /** Trailing element (e.g. collapse chevron for areas) */
  trailing?: ReactNode
  /** Sidebar drop target identifier for DnD */
  dropTarget?: string
  /** Red dot indicator (e.g. new tasks since last visit) */
  dot?: boolean
  /** Visually dim this item (e.g. later / someday projects) */
  dimmed?: boolean
  /** Whether this item is selected (multi-select) */
  selected?: boolean
  /** Selection index (1-based) to show in badge */
  selectionIndex?: number
  /** Whether multi-select mode is active (shows indicator on all items) */
  isSelectionMode?: boolean
  /** Item ID — needed for the selection toggle button */
  itemId?: string
  /** First in a contiguous selected run — rounded top corners */
  isFirstSelected?: boolean
  /** Last in a contiguous selected run — rounded bottom corners */
  isLastSelected?: boolean
}

const SidebarItem = memo(function SidebarItem({
  to,
  isActive,
  iconMode,
  onNavigate,
  onMouseEnter,
  onContextMenu,
  onClick,
  icon,
  label,
  labelEmpty,
  badge,
  alert,
  dueAt,
  avatars,
  trailing,
  dropTarget,
  dot,
  dimmed,
  selected,
  selectionIndex,
  isSelectionMode,
  itemId,
  isFirstSelected,
  isLastSelected,
}: SidebarItemProps) {
  // v1: no hover bg on items, only selected state gets a tinted background
  const activeBg = iconMode === "color" ? "bg-muted-foreground/10" : "bg-accent"

  // Subscribe directly to Zustand with a narrow selector — only re-renders
  // when THIS item's highlight status changes (not when any other item does).
  const isDropHighlighted = useViewContext(
    useCallback(
      (s: { isDragging: boolean; sidebarDropTarget: string | null }) =>
        s.isDragging &&
        dropTarget != null &&
        s.sidebarDropTarget === dropTarget,
      [dropTarget],
    ),
  )

  return (
    <NavLink
      to={to}
      onClick={onClick ?? onNavigate}
      onMouseEnter={onMouseEnter}
      onContextMenu={onContextMenu}
      data-sidebar-drop={dropTarget}
      className={cn(
        // v1: padding 8px 12px, borderRadius 8px, no gap between items
        "group/item flex min-w-0 items-center gap-2.5 px-3 py-2 text-base font-medium",
        "transition-colors duration-100",
        // Connected selection rounding — same pattern as TaskItem / checklist
        selected
          ? isFirstSelected && isLastSelected
            ? "rounded-lg"
            : isFirstSelected
              ? "rounded-t-lg"
              : isLastSelected
                ? "rounded-b-lg"
                : "rounded-none"
          : "rounded-lg",
        selected
          ? "bg-primary/10 text-foreground"
          : isDropHighlighted
            ? "bg-primary/15 text-foreground"
            : isActive
              ? `${activeBg} text-foreground`
              : "text-foreground",
        dimmed && !isActive && !selected && "opacity-50",
      )}
    >
      <SidebarItemContent
        icon={icon}
        label={label}
        labelEmpty={labelEmpty}
        badge={badge}
        alert={alert}
        dueAt={dueAt}
        avatars={avatars}
        trailing={trailing}
        dot={dot}
        isActive={isActive}
        dimmed={dimmed}
      />
      {/* Selection indicator — right side clickable circle (like tasks) */}
      {isSelectionMode && itemId && (
        <button
          type="button"
          className="ml-auto flex shrink-0 items-center transition-[opacity,transform] duration-150 ease-out"
          style={{ opacity: selected ? 1 : 0.3 }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            useViewContext.getState().toggleSelectSidebar(itemId)
          }}
          aria-label={selected ? "Deselect" : "Select"}
        >
          <DynamicIcon
            type="project"
            percentage={selected ? 100 : 0}
            unfinished={0}
            status={ObjectStatus.Unfinished}
            size="small"
            color={selected ? "var(--primary)" : "currentColor"}
            animate={false}
            label={selected ? selectionIndex : undefined}
          />
        </button>
      )}
    </NavLink>
  )
})



// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// SidebarMobileFooter — Settings centered + FAB (Plus+Chevron) bottom-right.
// v1 pattern: Settings icon+text centered in scroll area, FAB fixed over sidebar.
// ---------------------------------------------------------------------------

const SidebarMobileFooter = memo(function SidebarMobileFooter({
  iconMode,
  onNavigate,
}: {
  iconMode: IconMode
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  const handleAddAction = useCallback(
    async (type: "todo" | "project" | "list" | "area") => {
      switch (type) {
        case "todo": {
          const defaults = getTaskDefaults(viewId, containerId ?? undefined, eveningHour)
          const result = await addTask("", defaults.parentPath)
          if (!result.ok) break
          const id = result.data.id
          if (defaults.schedule) {
            await scheduleTask(id, defaults.schedule.startAt, defaults.schedule.type, defaults.schedule.subType)
          }
          useEditFocusStore.getState().requestEditFocus(id)
          navigate({ search: `?tid=${id}` })
          onNavigate?.()
          break
        }
        case "project": {
          const result = await addProject("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.PROJECT(result.data.id))
          onNavigate?.()
          break
        }
        case "list": {
          const result = await addList("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.LIST(result.data.id))
          onNavigate?.()
          break
        }
        case "area": {
          const result = await addArea("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.AREA(result.data.id))
          onNavigate?.()
          break
        }
      }
    },
    [viewId, containerId, eveningHour, navigate, onNavigate],
  )

  return (
    <>
      {/* Settings — centered with icon + text, matching v1 */}
      <button
        type="button"
        onClick={() => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev)
              next.set("setting", "general")
              // On mobile: show the settings menu first so user picks a category
              next.set("showSettingMenu", "true")
              return next
            },
            { replace: true },
          )
          onNavigate?.()
        }}
        className="mx-auto flex items-center gap-1 rounded-md px-4 mt-14 mb-5 text-muted-foreground/60 active:bg-accent"
      >
        <StaticIcon type="settings" size={24} mode={iconMode} active={false} />
        <span className="text-sm">{t("Settings")}</span>
      </button>

      {/* FAB — fixed bottom-right over sidebar, opens AddMenu popover */}
      <SidebarFab iconMode={iconMode} onAction={handleAddAction} />
    </>
  )
})

// ---------------------------------------------------------------------------
// SidebarFab — Fixed FAB (Plus + Chevron) that floats bottom-right in sidebar.
// v1 pattern: blue circular button that opens AddMenu popover on tap.
// ---------------------------------------------------------------------------

const SidebarFab = memo(function SidebarFab({
  iconMode,
  onAction,
}: {
  iconMode: IconMode
  onAction: (type: "todo" | "project" | "list" | "area") => void
}) {
  return (
    <div
      className="fixed z-50"
      style={{
        bottom: "calc(var(--safe-area-bottom, 0px) + 16px)",
        right: "16px",
      }}
    >
      <AddMenu iconMode={iconMode} onAction={onAction} fabMode />
    </div>
  )
})

// ---------------------------------------------------------------------------
// SidebarFooter — "+ Nytt" button + settings gear (desktop only)
// ---------------------------------------------------------------------------

const SidebarFooter = memo(function SidebarFooter({
  iconMode,
  onNavigate,
}: {
  iconMode: IconMode
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  const handleAddAction = useCallback(
    async (type: "todo" | "project" | "list" | "area") => {
      switch (type) {
        case "todo": {
          const defaults = getTaskDefaults(
            viewId,
            containerId ?? undefined,
            eveningHour,
          )
          const result = await addTask("", defaults.parentPath)
          if (!result.ok) break
          const id = result.data.id
          if (defaults.schedule) {
            await scheduleTask(
              id,
              defaults.schedule.startAt,
              defaults.schedule.type,
              defaults.schedule.subType,
            )
          }
          // Navigate to today (or current view) with the new task open for editing
          useEditFocusStore.getState().requestEditFocus(id)
          navigate({ search: `?tid=${id}` })
          onNavigate?.()
          break
        }
        case "project": {
          const result = await addProject("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.PROJECT(result.data.id))
          onNavigate?.()
          break
        }
        case "list": {
          const result = await addList("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.LIST(result.data.id))
          onNavigate?.()
          break
        }
        case "area": {
          const result = await addArea("")
          if (!result.ok) break
          useEditFocusStore.getState().requestEditFocus(result.data.id)
          navigate(ROUTES.AREA(result.data.id))
          onNavigate?.()
          break
        }
      }
    },
    [viewId, containerId, eveningHour, navigate, onNavigate],
  )

  return (
    <div className="flex shrink-0 items-center justify-between border-t border-border/10 px-5 py-3 pb-2.5">
      <AddMenu iconMode={iconMode} onAction={handleAddAction} />

      <button
        type="button"
        aria-label="Settings"
        onClick={() => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev)
              next.set("setting", "general")
              return next
            },
            { replace: true },
          )
          onNavigate?.()
        }}
        className={cn(
          "rounded-md p-1.5 text-muted-foreground",
          iconMode === "color"
            ? "hover:bg-muted-foreground/10"
            : "hover:bg-accent",
        )}
      >
        <StaticIcon type="settings" size={24} mode={iconMode} active={false} />
      </button>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Match route — checks if pathname starts with the given route. */
function isRouteActive(pathname: string, to: string): boolean {
  if (to === ROUTES.TODAY) {
    return pathname === "/" || pathname === "/today"
  }
  return pathname.startsWith(to)
}

/** Check if a dynamic menu item's route is active */
function isDynamicItemActive(pathname: string, item: MenuItem): boolean {
  return pathname === getItemRoute(item)
}

/** Get the route path for a dynamic menu item */
function getItemRoute(item: MenuItem): string {
  switch (item.type) {
    case ObjectType.Project:
      return ROUTES.PROJECT(item.id)
    case ObjectType.List:
      return ROUTES.LIST(item.id)
    case ObjectType.Area:
      return ROUTES.AREA(item.id)
    default:
      return "/"
  }
}

/** Get the sidebar drop target identifier for a dynamic menu item */
function getItemDropTarget(item: MenuItem): string {
  switch (item.type) {
    case ObjectType.Project:
      return `project:${item.id}`
    case ObjectType.List:
      return `list:${item.id}`
    case ObjectType.Area:
      return `area:${item.id}`
    default:
      return ""
  }
}


